import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { DeviceBreakdown } from "src/store/presenceSlice";

interface AnalyticsTableProps {
  devices: DeviceBreakdown[];
}

const AnalyticsTable: React.FC<AnalyticsTableProps> = ({ devices }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayLimit = 5;
  const displayedDevices = isExpanded
    ? devices
    : devices.slice(0, displayLimit);

  if (devices.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No device data available
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur-xl">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                Device Model
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                Platform
              </th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300 uppercase tracking-wider">
                Active Users
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {displayedDevices.map((device, idx) => (
              <tr key={idx} className="hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 text-sm text-white font-medium">
                  {device.deviceModel}
                </td>
                <td className="px-6 py-4 text-sm text-gray-300">
                  <div className="flex gap-2">
                    {Array.from(device.platforms).map((platform) => (
                      <span
                        key={platform}
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          platform.toLowerCase() === "android"
                            ? "bg-green-500/20 text-green-300"
                            : platform.toLowerCase() === "ios"
                              ? "bg-indigo-500/20 text-indigo-300"
                              : "bg-gray-500/20 text-gray-300"
                        }`}
                      >
                        {platform.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 text-right text-sm font-bold text-white">
                  {device.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {devices.length > displayLimit && (
        <div className="border-t border-white/10 bg-white/5">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full px-6 py-3 flex items-center justify-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show {devices.length - displayLimit} More Devices
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTable;
